import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { apiFetch } from './api';

function formatTimestamp(value) {
  if (!value) {
    return 'Ingen aktivitet ännu';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Okänd tid';
  }

  return new Intl.DateTimeFormat('sv-SE', {
    dateStyle: 'short',
    timeStyle: 'short'
  }).format(date);
}

export default function SiteAdminManager({ sites, accessToken }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [selectedSiteIds, setSelectedSiteIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [sendingUserId, setSendingUserId] = useState('');
  const [message, setMessage] = useState('');

  const sortedSites = useMemo(
    () => [...sites].sort((left, right) => left.display_name.localeCompare(right.display_name, 'sv')),
    [sites]
  );

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const payload = await apiFetch('/admin/site-admins', { accessToken });
      setUsers(payload.data || []);
    } catch (requestError) {
      setError(requestError.message || 'Kunde inte läsa webbplatsadministratörer.');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  function toggleSite(siteId) {
    setSelectedSiteIds((current) => (
      current.includes(siteId)
        ? current.filter((id) => id !== siteId)
        : [...current, siteId]
    ));
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!email.trim() || !selectedSiteIds.length) {
      setMessage('Ange e-post och välj minst en webbplats.');
      return;
    }

    setSubmitting(true);
    setMessage('');
    setError('');

    try {
      const payload = await apiFetch('/admin/site-admins', {
        accessToken,
        method: 'POST',
        body: {
          email,
          siteIds: selectedSiteIds
        }
      });

      const createdUser = payload.data;
      setEmail('');
      setSelectedSiteIds([]);
      setMessage(`Behörighet sparad för ${createdUser.email}.`);
      await loadUsers();
    } catch (requestError) {
      setError(requestError.message || 'Kunde inte spara webbplatsadministratören.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSendAccessEmail(userId) {
    setSendingUserId(userId);
    setMessage('');
    setError('');

    try {
      const payload = await apiFetch(`/admin/site-admins/${userId}/send-access-email`, {
        accessToken,
        method: 'POST',
        body: {}
      });

      setMessage(`Mejl skickat till ${payload.data.email}.`);
      await loadUsers();
    } catch (requestError) {
      setError(requestError.message || 'Kunde inte skicka åtkomstmejl.');
    } finally {
      setSendingUserId('');
    }
  }

  return (
    <section className="shell-card site-admin-manager">
      <div className="site-admin-manager__header">
        <div>
          <p className="eyebrow">Webbplatsadministratörer</p>
          <h2>Hantera konton och åtkomst</h2>
          <p className="muted-text">Lägg till e-postadresser, koppla dem till en eller flera webbplatser och skicka lösenordsmejl.</p>
        </div>
      </div>

      <form className="site-admin-form shell-subcard" onSubmit={handleSubmit}>
        <label className="field">
          <span>E-postadress</span>
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="admin@example.com"
            required
          />
        </label>

        <div className="site-admin-form__sites">
          <div className="site-admin-form__sites-header">
            <strong>Webbplatser</strong>
            <button
              type="button"
              className="button button--ghost button--small"
              onClick={() => setSelectedSiteIds(sortedSites.map((site) => site.site_id))}
            >
              Markera alla
            </button>
          </div>
          <div className="site-admin-form__site-list">
            {sortedSites.map((site) => (
              <label key={site.site_id} className="site-admin-form__site-option">
                <input
                  type="checkbox"
                  checked={selectedSiteIds.includes(site.site_id)}
                  onChange={() => toggleSite(site.site_id)}
                />
                <span>
                  <strong>{site.display_name}</strong>
                  <small>{site.repo_owner}/{site.repo_name}</small>
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="site-admin-form__actions">
          <button type="submit" className="button button--primary" disabled={submitting}>
            {submitting ? 'Sparar...' : 'Lägg till webbplatsadministratör'}
          </button>
        </div>
      </form>

      {error ? <p className="feedback feedback--error">{error}</p> : null}
      {message ? <p className="feedback feedback--success">{message}</p> : null}

      {loading ? <p>Laddar användare...</p> : null}

      {!loading ? (
        <div className="site-admin-list">
          {users.map((user) => (
            <article key={user.userId} className="shell-subcard site-admin-card">
              <div className="site-admin-card__header">
                <div>
                  <h3>{user.email}</h3>
                  <p className="muted-text">
                    {user.siteCount} webbplats(er) · Senaste inloggning {formatTimestamp(user.lastSignInAt)}
                  </p>
                </div>
                <button
                  type="button"
                  className="button button--ghost button--small"
                  disabled={sendingUserId === user.userId}
                  onClick={() => handleSendAccessEmail(user.userId)}
                >
                  {sendingUserId === user.userId ? 'Skickar...' : 'Skicka åtkomstmejl'}
                </button>
              </div>
              <div className="site-admin-card__sites">
                {user.sites.map((site) => (
                  <div key={`${user.userId}-${site.siteId}`} className="site-admin-card__site-pill">
                    <strong>{site.displayName}</strong>
                    <small>{site.repoOwner}/{site.repoName}</small>
                  </div>
                ))}
              </div>
            </article>
          ))}
          {!users.length ? <p className="workflow-status__empty">Inga webbplatsadministratörer ännu.</p> : null}
        </div>
      ) : null}
    </section>
  );
}